import { Component } from '@theme/component';

/**
 * Press marquee — arms the travelling logo row, and owns the one contract CSS
 * cannot express: whether the rail is a focusable scroll region.
 *
 * THE RULE. Focus follows capability. While the row travels it is not a scroll
 * container (`overflow: clip`, set in the section's stylesheet), so it carries
 * no tabindex, no group role and no accessible name — a tab stop that does
 * nothing when you land on it is worse than no tab stop. When the row is static
 * it IS a scroller, and every one of those comes back, because at narrow widths
 * scrolling is the only way to reach the logos past the first.
 *
 * The static case is not hypothetical: it is what a visitor who has asked for
 * reduced motion gets, and on a phone the row is more than three times the
 * width of the screen. Leaving it unscrollable there would lose content to an
 * accessibility preference.
 *
 * WHY REDUCED MOTION IS READ HERE AND NOT ONLY IN CSS. Attributes are not
 * styleable, so this has to be JavaScript. It listens for `change` rather than
 * reading `matches` once, so flipping the system setting mid-session moves the
 * tab stop with it instead of stranding the old state.
 *
 * THE OTHER GATE. `.is-animated` is only ever added here, so with JavaScript
 * unavailable the row never starts moving — and the markup ships as a static,
 * scrollable, focusable rail, which is the correct no-JS state on its own.
 * WCAG 2.2.2 wants a mechanism to pause anything that moves for more than five
 * seconds; with the rail no longer focusable, the pause button is now the ONLY
 * such mechanism, which is why it is revealed in lockstep with arming.
 */
class PressMarquee extends Component {
  /** @type {HTMLButtonElement | null} */
  #toggle = null;

  /** @type {HTMLElement | null} */
  #viewport = null;

  /** @type {MediaQueryList | null} */
  #reduced = null;

  /** The scroller's accessible name, held so the static state can restore it. */
  #label = '';

  mount() {
    this.#toggle = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-press-toggle]'));
    this.#viewport = /** @type {HTMLElement | null} */ (this.querySelector('[data-press-viewport]'));

    // No control means no licence to move. Bail before arming anything.
    if (!this.#toggle || !this.#viewport) return;

    this.#label = this.#viewport.getAttribute('aria-label') ?? '';

    this.on(this.#toggle, 'click', () => {
      this.classList.toggle('is-paused');
      this.#name();
    });

    this.#reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.on(this.#reduced, 'change', () => this.#sync());
    this.#sync();
  }

  /** Put the rail into whichever of the two states the visitor has asked for. */
  #sync() {
    const viewport = this.#viewport;
    const toggle = this.#toggle;
    if (!viewport || !toggle) return;

    const travels = !this.#reduced?.matches;

    this.classList.toggle('is-animated', travels);
    if (!travels) this.classList.remove('is-paused');

    // Nothing moves in the static state, so there is nothing to pause.
    toggle.hidden = !travels;

    if (travels) {
      viewport.removeAttribute('tabindex');
      viewport.removeAttribute('role');
      viewport.removeAttribute('aria-label');
    } else {
      viewport.setAttribute('tabindex', '0');
      viewport.setAttribute('role', 'group');
      if (this.#label) viewport.setAttribute('aria-label', this.#label);
    }

    this.#name();
  }

  /**
   * The name states the next action, which is the convention for a play/pause
   * toggle and the reason this is not `aria-pressed`.
   */
  #name() {
    const toggle = this.#toggle;
    if (!toggle) return;
    const paused = this.classList.contains('is-paused');
    const next = paused ? this.dataset.playLabel : this.dataset.pauseLabel;
    if (next) toggle.setAttribute('aria-label', next);
  }
}

if (!customElements.get('press-marquee')) {
  customElements.define('press-marquee', PressMarquee);
}

export {};
